import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

const PARTY = { D: "bg-sky-600", R: "bg-rose-600", I: "bg-violet-600", other: "bg-slate-500" };

export const KeyFigures = ({ figures }) => !figures?.length ? null : (
  <section className="surface p-5" data-testid="live-figures">
    <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Reported figures — each credited to who reported it</p>
    <div className="grid gap-3 sm:grid-cols-2">{figures.map((f) => (
      <div key={f.id} className="rounded-xl bg-[#F5F1EB] p-3" data-testid={`live-figure-${f.id}`}>
        <p className="font-serif text-3xl">{f.value}</p>
        <p className="text-sm font-medium text-slate-800">{f.label}</p>
        <p className="text-xs text-slate-500">Reported by {f.reported_by}{f.via && ` · via ${f.via}`}{f.as_of && ` · ${f.as_of}`}</p>
      </div>))}</div>
    <p className="mt-3 text-[11px] text-slate-400">Figures differ between sources and are shown side by side, not combined.</p>
  </section>
);

const RaceRow = ({ r }) => (
  <tr className="border-t border-border align-top" data-testid={`race-${r.label}`}>
    <td className="px-3 py-2 font-medium">{r.label}</td>
    <td className="px-3 py-2">{r.candidates?.length ? r.candidates.map((c) => (
      <span key={c.name} className="mr-3 inline-flex items-center gap-1.5"><span className={`h-2 w-2 rounded-full ${PARTY[c.party] || PARTY.other}`} />{c.name} ({c.party})
        {c.pct != null && <b>{c.pct}%</b>}{c.votes != null && <span className="text-slate-500">{Number(c.votes).toLocaleString()}</span>}</span>)) : <span className="text-slate-400">No verified results yet</span>}</td>
    <td className="px-3 py-2">{r.pct_counted != null ? `${r.pct_counted}%` : "–"}</td>
    <td className="px-3 py-2">{r.projected_winner ? <><b>{r.projected_winner}</b> ({r.projected_party}){r.projected_by && <span className="text-slate-500"> · called by {r.projected_by}</span>}</> : "–"}
      {r.via && <p className="text-[11px] text-slate-400">Source: {r.via}{r.as_of && ` · ${r.as_of}`}</p>}</td>
  </tr>
);

export const RacesTable = ({ liveId }) => {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [all, setAll] = useState(false);
  useEffect(() => { api.get(`/live/${liveId}/races`).then((r) => setD(r.data)).catch(() => {}); }, [liveId]);
  const rows = useMemo(() => (d?.races || []).filter((r) => (all || q || r.candidates?.length) && r.label.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true })), [d, q, all]);
  if (!d?.total) return null;
  return (
    <section className="space-y-3" data-testid="live-races">
      <div className="flex flex-wrap items-end gap-3"><div className="flex-1"><h2 className="text-2xl">Seats up for election ({d.total})</h2>
        <p className="text-xs text-slate-500" data-testid="races-summary">{d.with_results} with verified results · projected: {Object.entries(d.projected).map(([p, n]) => `${p} ${n}`).join(", ") || "none yet"}. Numbers come only from verified articles and are approved by an editor.</p></div>
        <Input className="w-44" placeholder="Search e.g. PA or TX-07" value={q} onChange={(e) => setQ(e.target.value)} data-testid="races-search" />
        <button className="text-sm font-medium text-[hsl(var(--facts))] hover:underline" onClick={() => setAll(!all)} data-testid="races-show-all">{all ? "Only seats with results" : "Show all seats"}</button></div>
      <div className="surface overflow-x-auto"><table className="w-full min-w-[44rem] text-left text-xs"><thead className="bg-[#F5F1EB]"><tr>{["Seat", "Latest numbers", "Counted", "Projection"].map((c) => <th key={c} className="px-3 py-2 font-semibold">{c}</th>)}</tr></thead>
        <tbody>{rows.length ? rows.map((r) => <RaceRow key={r.id} r={r} />) : <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-500" data-testid="races-empty">No verified results yet. Tap "Show all seats" to see every race.</td></tr>}</tbody></table></div>
    </section>
  );
};
